export const PUSH_INSTALLATION_STORAGE_KEY =
  "reelitpro.push.installation.v1";

const CANONICAL_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function getDefaultStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}
function getDefaultCrypto() {
  try {
    return typeof window !== "undefined" ? window.crypto : null;
  } catch {
    return null;
  }
}

export function isCanonicalPushInstallationId(value) {
  return (
    typeof value === "string" && CANONICAL_UUID_PATTERN.test(value.trim())
  );
}

function createFallbackUuid(cryptoApi) {
  if (typeof cryptoApi?.getRandomValues !== "function") return null;

  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const value = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");

  return [
    value.slice(0, 8),
    value.slice(8, 12),
    value.slice(12, 16),
    value.slice(16, 20),
    value.slice(20),
  ].join("-");
}

function createInstallationId(cryptoApi) {
  if (typeof cryptoApi?.randomUUID === "function") {
    try {
      const value = cryptoApi.randomUUID();
      if (isCanonicalPushInstallationId(value)) return value.toLowerCase();
    } catch {
      // Fall through to the secure getRandomValues implementation.
    }
  }
  return createFallbackUuid(cryptoApi);
}

export function getStoredPushInstallationId(
  storage = getDefaultStorage()
) {
  if (!storage) return null;
  try {
    const value = storage.getItem(PUSH_INSTALLATION_STORAGE_KEY);
    return isCanonicalPushInstallationId(value)
      ? value.trim().toLowerCase()
      : null;
  } catch {
    return null;
  }
}

export function getOrCreatePushInstallation(options = {}) {
  const storage =
    options.storage === undefined ? getDefaultStorage() : options.storage;
  const cryptoApi =
    options.cryptoApi === undefined ? getDefaultCrypto() : options.cryptoApi;

  if (!storage) {
    return {
      installationId: null,
      persisted: false,
      code: "installation_storage_unavailable",
    };
  }

  try {
    const storedValue = storage.getItem(PUSH_INSTALLATION_STORAGE_KEY);
    if (isCanonicalPushInstallationId(storedValue)) {
      return {
        installationId: storedValue.trim().toLowerCase(),
        persisted: true,
        code: null,
      };
    }

    const installationId = createInstallationId(cryptoApi);
    if (!installationId) {
      return {
        installationId: null,
        persisted: false,
        code: "installation_storage_unavailable",
      };
    }

    storage.setItem(PUSH_INSTALLATION_STORAGE_KEY, installationId);
    return { installationId, persisted: true, code: null };
  } catch {
    return {
      installationId: null,
      persisted: false,
      code: "installation_storage_unavailable",
    };
  }
}

export function getOrCreatePushInstallationId(options = {}) {
  return getOrCreatePushInstallation(options).installationId;
}

export function preservePushInstallationOnLogout(
  storage = getDefaultStorage()
) {
  return {
    installationId: getStoredPushInstallationId(storage),
    installationPreserved: true,
    remoteRevocationPerformed: false,
  };
}

export function clearPushInstallationIdForTests(
  storage = getDefaultStorage()
) {
  if (!storage) return false;
  try {
    storage.removeItem(PUSH_INSTALLATION_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}
